import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  Grid,
  Alert,
  IconButton,
  Paper
} from '@mui/material';
import { PhotoCamera, Close, Edit, Save, Cancel } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { BusinessUnitEnum } from '../../utils/constants';

// Resize an image data URL on the client to reduce payload/storage.
// Keeps aspect ratio and caps to max dimensions. Chooses JPEG by default
// for strong compression; preserves PNG for inputs that were PNG.
const resizeImage = (dataUrl, maxWidth = 256, maxHeight = 256) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let width = img.naturalWidth || img.width;
      let height = img.naturalHeight || img.height;

      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      const targetWidth = Math.round(width * scale);
      const targetHeight = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const isPng = dataUrl.startsWith('data:image/png');
      const mimeType = isPng ? 'image/png' : 'image/jpeg';
      const quality = mimeType === 'image/jpeg' ? 0.7 : 1.0;

      try {
        const resizedDataUrl = canvas.toDataURL(mimeType, quality);
        resolve(resizedDataUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });
};

const UserProfile = ({ open, onClose }) => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    profile_picture: user?.profile_picture || ''
  });
  
  // Password change state
  const [editingPassword, setEditingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleProfileUpdate = async () => {
    if (!profileData.name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.put('/api/users/profile', {
        name: profileData.name,
        email: profileData.email,
        profile_picture: profileData.profile_picture
      });
      
      updateUser(response.data);
      setSuccess('Profile updated successfully!');
      setEditingProfile(false);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      setError('All password fields are required');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await axios.post('/api/users/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      setSuccess('Password changed successfully!');
      setEditingPassword(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const originalDataUrl = e.target.result;
        let resizedDataUrl = originalDataUrl;
        try {
          resizedDataUrl = await resizeImage(originalDataUrl, 256, 256);
        } catch (resizeErr) {
          // Fallback to original if resizing fails
          console.error('Image resize failed, using original image.', resizeErr);
        }
        setProfileData({ ...profileData, profile_picture: resizedDataUrl });
        
        // Auto-save profile picture immediately for all users
        try {
          setLoading(true);
          setError(null);
          
          const response = await axios.put('/api/users/profile', {
            profile_picture: resizedDataUrl
          });
          
          // Update user context with new profile picture
          updateUser(response.data);
          setSuccess('Profile picture updated successfully!');
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to update profile picture');
          // Revert the profile picture on error
          setProfileData({ ...profileData, profile_picture: user?.profile_picture || '' });
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const resetProfileData = () => {
    setProfileData({
      name: user?.name || '',
      email: user?.email || '',
      profile_picture: user?.profile_picture || ''
    });
    setEditingProfile(false);
  };

  const resetPasswordData = () => {
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setEditingPassword(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        color: '#1a202c',
        fontWeight: 700,
        fontSize: '1.5rem',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        pb: 2
      }}>
        <Typography variant="h5" component="h2" sx={{ color: '#000000 !important', fontWeight: '700 !important' }}>
          User Profile
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#000000' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <AnimatePresence>
          {(success || error) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{ marginBottom: '16px' }}
            >
              <Alert 
                severity={success ? 'success' : 'error'} 
                onClose={() => { setSuccess(null); setError(null); }}
                sx={{
                  '& .MuiAlert-message': { color: success ? '#065f46' : '#991b1b' },
                  backgroundColor: success ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${success ? '#34d399' : '#f87171'}`
                }}
              >
                {success || (typeof error === 'string' ? error : JSON.stringify(error) || 'An error occurred')}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <Grid container spacing={3}>
          {/* Profile Picture Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              background: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={profileData.profile_picture}
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid rgba(0, 153, 153, 0.2)',
                    fontSize: '48px',
                    bgcolor: '#f0f9ff',
                    color: '#1a202c',
                    fontWeight: 600
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                
                {/* Profile picture is always editable for all users */}
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: '#009999',
                    color: 'white',
                    '&:hover': { bgcolor: '#007777' },
                    width: 40,
                    height: 40
                  }}
                >
                  <PhotoCamera />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </IconButton>
              </Box>
              
              <Typography variant="body1" sx={{ color: '#000000 !important', fontWeight: '600 !important' }}>
                Click camera to change photo
              </Typography>
            </Paper>
          </Grid>

          {/* Profile Information Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ 
              p: 3,
              background: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#000000 !important', fontWeight: '700 !important' }}>
                  Profile Information
                </Typography>
                {/* Only show edit button for admin users */}
                {user?.role === 'ADMIN' && !editingProfile ? (
                  <Button
                    startIcon={<Edit />}
                    onClick={() => setEditingProfile(true)}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#009999',
                      color: '#000000',
                      fontWeight: 700,
                      '&:hover': { borderColor: '#007777', bgcolor: 'rgba(0, 153, 153, 0.04)' }
                    }}
                  >
                    Edit
                  </Button>
                ) : user?.role === 'ADMIN' && editingProfile ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<Save />}
                      onClick={handleProfileUpdate}
                      variant="contained"
                      size="small"
                      disabled={loading}
                      sx={{
                        bgcolor: '#009999',
                        color: '#ffffff',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#007777' },
                        '&:disabled': { bgcolor: '#9ca3af', color: '#ffffff' }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      startIcon={<Cancel />}
                      onClick={resetProfileData}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#6b7280',
                        color: '#000000',
                        fontWeight: 700,
                        '&:hover': { borderColor: '#4a5568', bgcolor: 'rgba(107, 114, 128, 0.04)' }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : null}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    disabled={!editingProfile}
                    sx={{
                      '& .MuiInputLabel-root': { color: '#000000 !important', fontWeight: '600 !important' },
                      '& .MuiInputBase-input': { color: '#000000 !important', fontWeight: '600 !important' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                        '&:hover fieldset': { borderColor: '#009999' },
                        '&.Mui-focused fieldset': { borderColor: '#009999' }
                      },
                      // Fix autocomplete styling
                      '& .MuiAutocomplete-inputRoot': {
                        '& .MuiInputBase-input': { color: '#000000 !important' }
                      },
                      // Ensure disabled/static field readability
                      '& .Mui-disabled': {
                        '& .MuiInputBase-input': { color: '#000000 !important', WebkitTextFillColor: '#000000 !important' },
                        '& .MuiInputLabel-root': { color: '#4a5568 !important' }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    disabled={!editingProfile}
                    sx={{
                      '& .MuiInputLabel-root': { color: '#000000 !important', fontWeight: '600 !important' },
                      '& .MuiInputBase-input': { color: '#000000 !important', fontWeight: '600 !important' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                        '&:hover fieldset': { borderColor: '#009999' },
                        '&.Mui-focused fieldset': { borderColor: '#009999' }
                      },
                      // Fix autocomplete styling
                      '& .MuiAutocomplete-inputRoot': {
                        '& .MuiInputBase-input': { color: '#000000 !important' }
                      },
                      // Ensure disabled/static field readability
                      '& .Mui-disabled': {
                        '& .MuiInputBase-input': { color: '#000000 !important', WebkitTextFillColor: '#000000 !important' },
                        '& .MuiInputLabel-root': { color: '#4a5568 !important' }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Unit"
                    value={BusinessUnitEnum[user?.business_unit] || user?.business_unit || 'Not specified'}
                    disabled={true}
                    sx={{
                      '& .MuiInputLabel-root': { color: '#4a5568 !important', fontWeight: '600 !important' },
                      '& .MuiInputBase-input': { color: '#000000 !important', fontWeight: '600 !important', WebkitTextFillColor: '#000000 !important' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                        '&:hover fieldset': { borderColor: '#009999' },
                        '&.Mui-focused fieldset': { borderColor: '#009999' }
                      },
                      // Specifically target disabled state
                      '&.Mui-disabled, & .Mui-disabled': {
                        '& .MuiInputBase-input': { color: '#000000 !important', WebkitTextFillColor: '#000000 !important' }
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Password Change Section */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ 
              p: 3,
              background: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#000000 !important', fontWeight: '700 !important' }}>
                  Password & Security
                </Typography>
                {!editingPassword ? (
                  <Button
                    startIcon={<Edit />}
                    onClick={() => setEditingPassword(true)}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#009999',
                      color: '#000000',
                      fontWeight: 700,
                      '&:hover': { borderColor: '#007777', bgcolor: 'rgba(0, 153, 153, 0.04)' }
                    }}
                  >
                    Change Password
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<Save />}
                      onClick={handlePasswordChange}
                      variant="contained"
                      size="small"
                      disabled={loading}
                      sx={{
                        bgcolor: '#009999',
                        color: '#ffffff',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#007777' },
                        '&:disabled': { bgcolor: '#9ca3af', color: '#ffffff' }
                      }}
                    >
                      Change
                    </Button>
                    <Button
                      startIcon={<Cancel />}
                      onClick={resetPasswordData}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#6b7280',
                        color: '#000000',
                        fontWeight: 700,
                        '&:hover': { borderColor: '#4a5568', bgcolor: 'rgba(107, 114, 128, 0.04)' }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>

              {editingPassword && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#000000 !important', fontWeight: '600 !important' },
                        '& .MuiInputBase-input': { color: '#000000 !important', fontWeight: '600 !important' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                          '&:hover fieldset': { borderColor: '#009999' },
                          '&.Mui-focused fieldset': { borderColor: '#009999' }
                        },
                        // Override browser autocomplete styling
                        '& input:-webkit-autofill': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important',
                          backgroundColor: '#ffffff !important'
                        },
                        '& input:-webkit-autofill:hover': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important'
                        },
                        '& input:-webkit-autofill:focus': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#000000 !important', fontWeight: '600 !important' },
                        '& .MuiInputBase-input': { color: '#000000 !important', fontWeight: '600 !important' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                          '&:hover fieldset': { borderColor: '#009999' },
                          '&.Mui-focused fieldset': { borderColor: '#009999' }
                        },
                        // Override browser autocomplete styling
                        '& input:-webkit-autofill': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important',
                          backgroundColor: '#ffffff !important'
                        },
                        '& input:-webkit-autofill:hover': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important'
                        },
                        '& input:-webkit-autofill:focus': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#000000 !important', fontWeight: '600 !important' },
                        '& .MuiInputBase-input': { color: '#000000 !important', fontWeight: '600 !important' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                          '&:hover fieldset': { borderColor: '#009999' },
                          '&.Mui-focused fieldset': { borderColor: '#009999' }
                        },
                        // Override browser autocomplete styling
                        '& input:-webkit-autofill': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important',
                          backgroundColor: '#ffffff !important'
                        },
                        '& input:-webkit-autofill:hover': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important'
                        },
                        '& input:-webkit-autofill:focus': {
                          WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
                          WebkitTextFillColor: '#000000 !important'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: '#6b7280',
            color: '#1a202c',
            fontWeight: 600,
            px: 3,
            '&:hover': { borderColor: '#4a5568', bgcolor: 'rgba(107, 114, 128, 0.04)' }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfile;
